/**
 * UserProfile Component
 *
 * Microsoft-style user profile dropdown component that displays in the top-right corner
 * of the application. Provides user information, account management, and logout functionality
 * similar to the Azure Portal interface.
 */

import React, { useState, forwardRef } from 'react';
import {
  Menu,
  Avatar,
  Group,
  Text,
  Divider,
  Button,
  Paper,
  Stack,
  Badge,
  ActionIcon,
  Tooltip,
  Box,
  Loader
} from '@mantine/core';
import {
  IconUser,
  IconLogout,
  IconClock,
  IconShield,
  IconChevronDown,
  IconMail,
  IconBuilding
} from '@tabler/icons-react';
import { useAuth } from '../shared/contexts/AuthContext';

/**
 * User avatar with initials fallback (similar to Microsoft style)
 */
const UserAvatar = forwardRef(({ user, size = 'sm', showChevron = false, ...props }, ref) => {
  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  const getAvatarColor = (email) => {
    if (!email) return 'blue';
    const colors = ['blue', 'green', 'orange', 'red', 'violet', 'teal', 'cyan'];
    const hash = email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  return (
    <Group gap="xs" ref={ref} {...props}>
      <Avatar
        size={size}
        color={getAvatarColor(user?.email)}
        radius="xl"
      >
        {getInitials(user?.name)}
      </Avatar>
      {showChevron && <IconChevronDown size={14} />}
    </Group>
  );
});

UserAvatar.displayName = 'UserAvatar';

/**
 * Session timeout warning component
 */
const SessionWarning = ({ sessionTimeoutWarning, extendSession }) => {
  if (!sessionTimeoutWarning) return null;

  return (
    <Paper p="md" bg="yellow.0" withBorder>
      <Stack gap="xs">
        <Group gap="xs">
          <IconClock size={16} color="orange" />
          <Text size="sm" fw={500} c="orange.7">
            Session Expiring Soon
          </Text>
        </Group>
        <Text size="xs" c="dimmed">
          Your session will expire in 5 minutes. Please save your work.
        </Text>
        <Button
          size="xs"
          variant="light"
          color="orange"
          onClick={extendSession}
          fullWidth
        >
          Extend Session
        </Button>
      </Stack>
    </Paper>
  );
};

/**
 * UserProfile Dropdown Component
 */
const UserProfile = ({ position = 'top-right' }) => {
  const {
    user,
    logout,
    isLoading,
    sessionTimeoutWarning,
    extendSession
  } = useAuth();

  const [menuOpened, setMenuOpened] = useState(false);

  const handleLogout = async () => {
    setMenuOpened(false);
    await logout();
  };

  if (isLoading || !user) {
    return (
      <Tooltip label="Loading user profile">
        <ActionIcon variant="light" size="lg" loading>
          <Loader size="xs" />
        </ActionIcon>
      </Tooltip>
    );
  }

  return (
    <Menu
      width={280}
      position={position}
      opened={menuOpened}
      onChange={setMenuOpened}
      withArrow
      arrowPosition="side"
      offset={5}
    >
      <Menu.Target>
        <ActionIcon
          variant="subtle"
          size="lg"
          style={{ cursor: 'pointer' }}
          aria-label="User profile menu"
        >
          <UserAvatar user={user} showChevron />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>
        <Box p="md">
          {/* User Information Header */}
          <Group gap="md" mb="sm">
            <UserAvatar user={user} size="lg" />
            <Stack gap={2} style={{ flex: 1 }}>
              <Text fw={600} size="sm" lineClamp={1}>
                {user.name || 'User'}
              </Text>
              <Text size="xs" c="dimmed" lineClamp={1}>
                {user.email}
              </Text>
              <Badge size="xs" variant="light" color="blue">
                Authenticated
              </Badge>
            </Stack>
          </Group>

          {/* Session Warning */}
          {sessionTimeoutWarning && (
            <Box mb="md">
              <SessionWarning
                sessionTimeoutWarning={sessionTimeoutWarning}
                extendSession={extendSession}
              />
            </Box>
          )}

          <Divider mb="sm" />

          {/* Account Information */}
          <Stack gap="xs" mb="md">
            <Group gap="xs">
              <IconMail size={14} />
              <Text size="xs" c="dimmed">
                {user.email}
              </Text>
            </Group>
            <Group gap="xs">
              <IconBuilding size={14} />
              <Text size="xs" c="dimmed">
                Presidio Network Solutions
              </Text>
            </Group>
            <Group gap="xs">
              <IconShield size={14} />
              <Text size="xs" c="dimmed">
                Azure Entra ID
              </Text>
            </Group>
          </Stack>

          <Divider mb="sm" />

          {/* Platform Access */}
          <Box mb="md">
            <Text size="xs" fw={500} c="dimmed" mb="xs">
              Platform Access
            </Text>
            <Group gap="xs">
              <Badge size="xs" color="orange" variant="light">AWS Connect</Badge>
              <Badge size="xs" color="blue" variant="light">Cisco WxCC</Badge>
              <Badge size="xs" color="green" variant="light">Five9</Badge>
              <Badge size="xs" color="purple" variant="light">SA Tools</Badge>
            </Group>
          </Box>

          <Divider mb="sm" />

          {/* Actions */}
          <Stack gap="xs">
            <Group gap="xs" style={{ cursor: 'pointer' }} p="xs" onClick={() => extendSession()}>
              <IconClock size={14} />
              <Text size="xs">Extend Session</Text>
            </Group>

            <Button
              variant="light"
              color="red"
              size="xs"
              fullWidth
              leftSection={<IconLogout size={14} />}
              onClick={handleLogout}
              loading={isLoading}
            >
              Sign Out
            </Button>
          </Stack>

          {/* Footer */}
          <Box mt="md" pt="sm" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
            <Text size="xs" c="dimmed" ta="center">
              Secure session • Auto-logout in 60 min
            </Text>
          </Box>
        </Box>
      </Menu.Dropdown>
    </Menu>
  );
};

/**
 * Compact UserProfile for smaller screens or embedded usage
 */
export const CompactUserProfile = () => {
  const { user, logout, isLoading } = useAuth();

  if (isLoading || !user) {
    return <Loader size="xs" />;
  }

  return (
    <Menu width={200} position="bottom-end">
      <Menu.Target>
        <ActionIcon variant="subtle" size="md">
          <UserAvatar user={user} size="sm" />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>{user.name}</Menu.Label>
        <Menu.Item
          leftSection={<IconUser size={14} />}
          disabled
        >
          {user.email}
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          leftSection={<IconLogout size={14} />}
          color="red"
          onClick={logout}
        >
          Sign Out
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};

export default UserProfile;